import commaSeparate from "../components/DealAnalyzerComponents/utils/commaSeparate";
import { schedule } from "../constants";
import hull from "hull.js";
import countiesList from "../constants/states_counties_cities.json";
import { toast } from 'react-toastify';

/**
 *
 * @param {Ref} element a ref of the element to scroll to the top off
 */
const scrollToTop = (element) => {
  element.current.scroll({
    top: 0,
    left: 0,
    behavior: "smooth",
  });
};

/**
 *
 * @param {number} days the number days to calculate for after today (including today)
 * @returns an array with key display in the form (Mar 30, Monday) and iso date of as value
 */
const getDatesFromToday = (days = 5, weekday = "long") => {
  const dates = [];
  for (let i = 0; i < days; i++) {
    dates.push({
      display: new Date(new Date().setDate(new Date().getDate() + i))
        .toLocaleDateString("en", {
          month: "short",
          weekday,
          day: "numeric",
        })
        .replace(",", "")
        .split(" "),
      value: new Date().setDate(new Date().getDate() + i),
    });
  }

  return dates;
};

//get name from string with underscore and capitalize first letter
const removeUnderandCapitalize = (string) => {
  return string
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 *
 * @param {*} number number to format
 * @param {*} decimals number of decimals to show, default 2
 * @returns formatted number
 */
const formatNumber = (number, decimals = 2) =>
  Number(number).toLocaleString("en-US", {
    maximumFractionDigits: decimals,
  });

/**
 *
 * @param {*} url non formatted url
 * @returns url with embedd
 */
function getYoutubeEmbedLink(url) {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);

  return match && match[2].length === 11 ? match[2] : null;
}

/**
 *
 * @param {[]} subs
 * @returns
 */

const getSubscriptionStatus = (subs) => {
  const status = {
    bpoFarm: false,
    premierAgentWebsite: false,
    liveLeads: false,
    dealAnalyzer: false,
    indPro: false,
    indElite: false,
    indUltra: false,
    contactLeads: false,
    package: "",
    currentPeriodStart: new Date().toJSON(),
    currentPeriodEnd: new Date().toJSON(),
  };

  if (subs.length === 0) {
    return status;
  }
  if (subs.some((item) => item.package === "bpo-farm")) {
    status.bpoFarm = true;
    status.premierAgentWebsite = true;
    status.liveLeads = true;
    status.dealAnalyzer = true;
    status.contactLeads = subs.some(
      (item) => item.package === "leads" && item.status === "active"
    );
  }
  if (subs.some((item) => item.package === "combo-package")) {
    status.dealAnalyzer = true;
    status.premierAgentWebsite = true;
    status.liveLeads = true;
  }
  if (subs.some((item) => item.package === "premier-agent-website")) {
    status.premierAgentWebsite = true;
    status.dealAnalyzer = true;
    status.liveLeads = true;
  }
  if (
    subs.some((item) => item.package === "leads" && item.status === "active")
  ) {
    status.contactLeads = true;
  }
  if (subs.some((item) => item.package === "deal-analyzer")) {
    status.dealAnalyzer = true;
  }
  if (subs.some((item) => item.package === "bpo-realty-agent")) {
    status.bpoFarm = true;
    status.premierAgentWebsite = true;
    status.liveLeads = true;
    status.dealAnalyzer = true;
    status.contactLeads = subs.some(
      (item) => item.package === "leads" && item.status === "active"
    );
  }
  if (subs.some((item) => item.package === "industry_professional_ultra")) {
    return {
      indPro: true,
      indElite: true,
      indUltra: true,
      contactLeads: subs.some(
        (item) => item.package === "leads" && item.status === "active"
      ),
    };
  }
  if (subs.some((item) => item.package === "industry_professional_elite")) {
    return {
      indPro: true,
      indElite: true,
      indUltra: false,
      contactLeads: subs.some(
        (item) => item.package === "leads" && item.status === "active"
      ),
    };
  }
  if (subs.some((item) => item.package === "industry_professional_pro")) {
    return {
      indPro: true,
      indElite: false,
      indUltra: false,
      contactLeads: subs.some(
        (item) => item.package === "leads" && item.status === "active"
      ),
    };
  }

  const premierAgentPackages = [
    "bpo-farm",
    "combo-package",
    "premier-agent-website",
    "bpo-realty-agent",
    "leads",
  ];

  // Set current active subscription period info
  const activePremierAgents =
    subs.filter(
      (item) =>
        item.status === "active" && premierAgentPackages.includes(item.package)
    ) || [];

  if (activePremierAgents.length > 0) {
    status.package = "premier-agent-website"; // All packages activates premier agent website features
    status.currentPeriodStart = activePremierAgents.length
      ? activePremierAgents.reduce(
        (min, item) =>
          item.current_period_start < min ? item.current_period_start : min,
        activePremierAgents[0].current_period_start
      )
      : null;
    status.currentPeriodEnd = activePremierAgents.length
      ? activePremierAgents.reduce(
        (max, item) =>
          item.current_period_end > max ? item.current_period_end : max,
        activePremierAgents[0].current_period_end
      )
      : null;
  }

  return status;
};

/**
 *
 * @param {string} url URL to check
 * @returns a url with https:// if it doesn't have it
 */
const getValidUrl = (url) => {
  if (!url) {
    return "";
  }
  if (url.includes("://")) return url;
  else return "https://" + url;
};

/**
 *
 * @param {string} phoneNumberString number to return in the format +1 (123) 456-7890
 * @returns
 */
function formatPhoneNumber(phoneNumberString) {
  var cleaned = ("" + phoneNumberString).replace(/\D/g, "");
  var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    // var intlCode = match[1] ? '+1 ' : '';
    return ["(", match[2], ") ", match[3], "-", match[4]].join("");
  }
  return cleaned || null;
}

function convertCurrencySystem(labelValue) {
  // Nine Zeroes for Billions
  return Math.abs(Number(labelValue)) >= 1.0e9
    ? (Math.abs(Number(labelValue)) / 1.0e9).toFixed(2) + "B"
    : // Six Zeroes for Millions
    Math.abs(Number(labelValue)) >= 1.0e6
      ? (Math.abs(Number(labelValue)) / 1.0e6).toFixed(2) + "M"
      : // Three Zeroes for Thousands
      Math.abs(Number(labelValue)) >= 1.0e3
        ? (Math.abs(Number(labelValue)) / 1.0e3).toFixed(2) + "K"
        : Math.abs(Number(labelValue));
}

function getAllDaysInMonth(year, month) {
  const today = new Date().getDate();
  const date = new Date(year, month, today);

  const dates = [];

  while (date.getMonth() === month) {
    dates.push(new Date(date).toDateString());
    date.setDate(date.getDate() + 1);
  }

  return dates;
}

/**
 *
 * @param {string} value get type of farm subscription
 * @returns position of farm subscription 0-starter, 1-pro, 2-elite
 */
const getPosition = (value) => {
  switch (value) {
    case "ultra":
      return 3;
    case "elite":
      return 2;
    case "pro":
      return 1;
    default:
      return 0;
  }
};

//add days to date
const addDays = (date, days = 1) => {
  let result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};
const checkDate = (date, days) => {
  const today = new Date();
  let compDate = date;
  let out = compDate;
  do {
    compDate = addDays(compDate, days);
    out = compDate;
  } while (
    new Date(today.setHours(0, 0, 0, 0)).getTime() >=
    new Date(compDate.setHours(0, 0, 0, 0)).getTime()
  );
  return out;
};

const formatDate = (date) =>
  date.toLocaleDateString("en-US", { month: "short", day: "numeric" });

/**
 *
 * @param {string} type type of subscription
 * @param {Date} createdDate created date of subscription
 * @param {Date} updatedDate updated date of subscription
 * @returns Array of timelines
 */
const getTimelines = (type, createdDate, updatedDate) => {
  return schedule.map((item) => {
    let start = formatDate(
      addDays(updatedDate, item[`${type}_days_to_add`] - 3)
    );
    let end = formatDate(addDays(updatedDate, item[`${type}_days_to_add`]));
    if (["Seller Calling", "Post Card Mailers"].includes(item.name)) {
      start = formatDate(
        checkDate(addDays(createdDate, -3), item[`${type}_days_to_add`])
      );
      end = formatDate(checkDate(createdDate, item[`${type}_days_to_add`]));
    }

    return `Scheduled ${item.name} ${start} - ${end}`;
  });
};

const commaReplace = (value) => {
  if (value) {
    const valueString = value.toString().replace(/[^0-9.-]/g, "");
    return commaSeparate(valueString);
  }
  return "";
};

const dataURLtoFile = (dataurl, filename) => {
  var arr = dataurl.split(","),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
};

/**
 *
 * @param {{parcels:{features}}} territory a territory object with parcels.features
 * @returns the flattened coordinates of the territory outline
 */
const getTerritoriesOutline = (territory) => {
  const coordinates = territory?.parcels?.features
    ?.map((item) =>
      item?.geometry?.coordinates[0][0].map((el) => [el[0], el[1]])
    )
    ?.flat();
  return hull(coordinates, 20);
};

/**
 *
 * @param {Number} value agent amount to check
 * @returns agent tier number
 */
const getTier = (value) => String(value <= 500 ? 1 : value <= 700 ? 2 : 3);

/**
 *
 * @param {string} id id of element to scroll
 * @param {Boolean} left scroll to the left or right
 */
const scrollToTheLeft = (id, left) => {
  const element = document.getElementById(id);
  //get width of element
  const width = element.offsetWidth / 2;
  //scroll by half
  if (left) {
    element.scrollLeft -= width / 2;
  } else {
    element.scrollLeft += width / 2;
  }
};

/**
 *
 * @param {Ref} ref ref of element to check
 * @param {*} id id of element to check for overflow
 */
const hideButton = (reference, id) => {
  const div = document.getElementById(id);
  const isWidthOverflowing = div.scrollWidth > div.clientWidth;
  if (reference && !isWidthOverflowing) {
    reference.style.display = "none";
  }
};

/**
 *
 * @param {number} number number to shorten
 * @returns shortened number (1.2M, 1.2K, 1.2B)
 */
const getPriceInShort = (number) => {
  const formatter = new Intl.NumberFormat("en-US", { notation: "compact" });
  return formatter.format(number);
};

function validateEmail(email) {
  var regex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
  return regex.test(email);
}

/**
 *
 * @param {*} token
 * @returns Boolean
 */
function isJwtExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const exp = payload.exp * 1000;
    return Date.now() >= exp;
  } catch {
    return true;
  }
}

const getCounties = (state) => {
  if (!state) return [];
  return Object.keys(countiesList[state]);
};
const getCities = (state, county) => {
  if (!state || !county) return [];

  const countyData = countiesList[state][county];
  if (Array.isArray(countyData)) {
    return countyData;
  }
  return Object.keys(countyData);
};

const getZipCodes = (state, county, city) => {
  if (!state || !county || !city) return [];

  const countyData = countiesList[state][county];
  if (Array.isArray(countyData)) {
    return [];
  }
  return countyData[city] || [];
};

const fullName = (person) => {
  if (!person) return null;

  if (person) {
    return `${person?.first_name || person?.firstName || ""} ${person?.last_name || person?.lastName || ""
      }`;
  }
};

const formatArrayNames = (arr) => {
  if (Array.isArray(arr) && arr.length > 0) {
    return arr
      .map((item) => item.name)
      .filter(Boolean)
      .join(", ");
  }
  return "-";
};

const handleLeadTypeColors = (value) => {
  switch (value) {
    case "nod":
      return "#EEF4FF";

    case "divorce":
      return "#F0F9FF";

    case "probate":
      return "#FFFAEB";

    case "expired":
      return "#ECFDF3";

    case "fsbo":
      return "#FDF2FA";

    case "withdrawn":
      return "#FAFAFA";

    case "canceled":
      return "#F4F3FF";

    default:
      return "#FEF6EE";
  }
};

const handleLeadTypeBorderColors = (value) => {
  switch (value) {
    case "nod":
      return "#C7D7FE";

    case "divorce":
      return "#B9E6FE";

    case "probate":
      return "#FEDF89";

    case "expired":
      return "#ABEFC6";

    case "fsbo":
      return "#FCCEEE";

    case "withdrawn":
      return "#E9EAEB";

    case "canceled":
      return "#D9D6FE";

    default:
      return "#F9DBAF";
  }
};

const handleLeadTypeTextColors = (value) => {
  switch (value) {
    case "nod":
      return "#3538CD";

    case "divorce":
      return "#026AA2";

    case "probate":
      return "#B54708";

    case "expired":
      return "#067647";

    case "fsbo":
      return "#C11574";

    case "withdrawn":
      return "#414651";

    case "canceled":
      return "#5925DC";

    default:
      return "#B93815";
  }
};

const leadTypeName = (value) => {
  switch (value) {
    case "nod":
      return "NOD";

    case "divorce":
      return "Divorce";

    case "probate":
      return "Probate";

    case "expired":
      return "Expired";

    case "fsbo":
      return "FSBO";

    case "withdrawn":
      return "Withdrawn";

    case "canceled":
      return "Canceled";

    default:
      return "Tax Lien";
  }
};

const noteLeadType = (value) => {
  switch (value) {
    case "nod":
      return "lead";

    case "divorce":
      return "divorce";

    case "probate":
      return "probate";

    case "expired":
      return "expired";

    case "fsbo":
      return "fsbo";

    case "withdrawn":
      return "withdrawn";

    case "canceled":
      return "canceled";

    default:
      return "taxlien";
  }
};

const formatCurrency = (value) => {
  if (typeof value === "number") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }
  return value || "-";
};

export const transformSubPrices = (pricing = []) => {
  return pricing.map((p) => {
    const { metadata } = p;
    if (metadata?.product === "agent_and_investor_list") {
      return {
        ...p,
        id: "combo",
        name: "Combo (Agent & Investor)",
        description: "Access to verified investor + agents contacts",
      };
    } else if (metadata?.product === "agent_list") {
      return {
        ...p,
        id: "agent",
        name: "Agent List Only",
        description: "Access to verified investor contacts",
      };
    } else if (metadata?.product === "investor_list") {
      return {
        ...p,
        id: "investor",
        name: "Investor List Only",
        description: "Access to verified agent contacts",
      };
    } else {
      return p;
    }
  });
};

const formatShortDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

function normalizeAmount(value, rule = "positive") {
  if (value === "" || value === "-") return value;

  const num = Number(value);

  if (rule === "positive") {
    return num < 0 ? 0 : num;
  }

  if (rule === "negativeOrZero") {
    if (num > 0) {
      toast.warn("This field accepts only negative values or zero");
      return 0;
    }
    return num;
  }

  return num;
}

const normalizeSaleDate = (value) => {
  if (!value) return null;

  //* Already ISO → keep it
  if (typeof value === "string" && value.includes("T")) {
    return value;
  }

  const date = new Date(value);

  return isNaN(date.getTime()) ? null : date.toISOString();
};

function handleNumericInput({
  value,
  comparableId,
  fieldKey,
  allow = "any",
  onChange,
  showWarning,
}) {
  //* Always strip commas first
  const rawValue = String(value).replace(/,/g, "");

  // Allow empty string and "-" while typing
  if (rawValue === "" || rawValue === "-") {
    onChange(comparableId, fieldKey, rawValue);
    return;
  }

  // Only digits with optional leading minus
  if (!/^-?\d*$/.test(rawValue)) {
    showWarning?.("Only numbers are allowed");
    return;
  }

  const num = Number(rawValue);

  // Enforce negativeOrZero rule
  if (allow === "negativeOrZero" && num > 0) {
    showWarning?.("This field accepts only negative values or zero");
    return;
  }

  //* Always store RAW number string
  onChange(comparableId, fieldKey, rawValue);
}

const getComparableData = (
  comp,
  customerGeneralInfoGarage,
  customerGeneralInfoLivableSquareFootage,
) => {
  // Sanitize inputs
  const compPrice = Number(String(comp.sales_price || "0").replace(/,/g, ""));
  const lotSize = Number(String(comp.lot_size || "0").replace(/,/g, ""));

  const constructionCost = Number(comp.new_construction_price || 0);

  const compGarage = Number(comp.garage || 0);
  const subjectGarage = Number(customerGeneralInfoGarage || 0);

  const compLivableSqft = Number(
    String(comp.gross_living_area || "0").replace(/,/g, ""),
  );

  // Replacement cost
  const replacementCost = compLivableSqft * constructionCost;

  // Price difference
  const priceDifference = replacementCost - compPrice;

  // Lot rate per sqft (avoid divide by zero)
  const lotRatePerSqft = lotSize > 0 ? priceDifference / lotSize : 0;

  //* Gross living area

  const garageValue = (compLivableSqft / 2) * 225;

  // Garage adjustment logic
  let garageDifference = 0;

  if (subjectGarage >= compGarage) {
    garageDifference = garageValue;
  } else {
    garageDifference = -garageValue;
  }

  //* livable square footage
  const generalInfoLivableSqft = Number(
    String(customerGeneralInfoLivableSquareFootage || "0").replace(/,/g, ""),
  );
  const compPricePerSquareFoot = Number(
    String(comp.price_per_gross_living_area || "0").replace(/,/g, ""),
  );

  const livableDifference = generalInfoLivableSqft - compLivableSqft;

  const livableSquareFtDifferenceAmount =
    compPricePerSquareFoot * livableDifference;

  return {
    ...comp,
    price_adjustment_lot_size: lotRatePerSqft,
    price_adjustment_interior_livable_area: livableSquareFtDifferenceAmount,
    price_adjustment_garage: garageDifference,
  };
};

//* Add commas
const formatWithCommas = (value) => {
  // allow typing states
  if (value === "" || value === "-" || value === null || value === undefined) {
    return value ?? "";
  }

  const num = Number(value);

  if (isNaN(num)) return "";

  return num.toLocaleString("en-US");
};

//* Remove commas
const stripCommas = (value) => {
  if (!value) return "";
  return String(value).replace(/,/g, "");
};

// Highlight matching text in query
function highlightMatch(text = "", query = "") {
  const normalizedText = String(text);
  const normalizedQuery = String(query);
  if (!normalizedQuery) return normalizedText;

  const lowerText = normalizedText.toLowerCase();
  const lowerQuery = normalizedQuery.toLowerCase();
  const matchStart = lowerText.indexOf(lowerQuery);
  if (matchStart === -1) return normalizedText;

  const matchEnd = matchStart + lowerQuery.length;
  return (
    <>
      {normalizedText.slice(0, matchStart)}
      <span
        style={{
          backgroundColor: "rgba(66, 165, 245, 0.35)",
          fontWeight: 700,
        }}
      >
        {normalizedText.slice(matchStart, matchEnd)}
      </span>
      {normalizedText.slice(matchEnd)}
    </>
  );
}

const toBase64 = async (url) => {
  if (!url) return null;
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) return null;
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

export {
  scrollToTop,
  getDatesFromToday,
  removeUnderandCapitalize,
  formatNumber,
  getYoutubeEmbedLink,
  getSubscriptionStatus,
  getValidUrl,
  handleLeadTypeTextColors,
  handleLeadTypeBorderColors,
  formatPhoneNumber,
  leadTypeName,
  noteLeadType,
  handleLeadTypeColors,
  convertCurrencySystem,
  getAllDaysInMonth,
  getPosition,
  getTimelines,
  commaReplace,
  dataURLtoFile,
  getTerritoriesOutline,
  getTier,
  scrollToTheLeft,
  hideButton,
  getPriceInShort,
  validateEmail,
  isJwtExpired,
  formatShortDate,
  getCities,
  getCounties,
  getZipCodes,
  fullName,
  formatArrayNames,
  formatCurrency,
  highlightMatch,
  toBase64
};
